import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext.jsx';
import asistenteIA from '../services/api/asistenteIA.js';

/**
 * Estado global del Asistente IA — puerto de
 * `IbuFront/src/components/contexts/IAChatContext.js` (V1) a IbuFront2. Comparte la
 * misma conversación activa entre el widget flotante (`ChatWidget`) y la página
 * dedicada (`pages/asistente-ia/AsistenteIAPage`), igual que V1 compartía entre
 * `AsistenteIAWidget` y `AsistenteIAPage` vía este mismo contexto.
 *
 * Se monta condicionalmente (ver `AppLayout.jsx` y `AsistenteIAPage.jsx`) sólo
 * cuando `hasPermiso('asistente-ia:usar')` — así nunca dispara `listarConversaciones`
 * para un usuario sin el permiso (evita el 403 innecesario, ver DUDAS.md si aplica).
 */
export const IAChatContext = createContext(null);

const OPEN_KEY = 'ibu_ia_open';

const mapMensaje = (m) => ({
  id: m._id || crypto.randomUUID(),
  role: m.role,
  text: m.text,
  chart: m.chart || null,
  charts: m.charts || (m.chart ? [m.chart] : []),
  rawData: m.rawData || null,
  error: !!m.error,
});

export function IAChatProvider({ children }) {
  const { isAuth, user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [conversaciones, setConversaciones] = useState([]); // [{_id, titulo, updatedAt}]
  const [activeId, setActiveId] = useState(null);
  const [activeMensajes, setActiveMensajes] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setConversaciones([]);
    setActiveId(null);
    setActiveMensajes([]);
    if (!isAuth || !user) return;
    asistenteIA
      .listarConversaciones()
      .then((body) => setConversaciones(body.data || []))
      .catch(() => setConversaciones([]));
  }, [isAuth, user]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, isOpen ? '1' : '0');
    } catch {
      // localStorage puede fallar en modo privado — no es crítico, se ignora.
    }
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const nuevaConversacion = useCallback(() => {
    setActiveId(null);
    setActiveMensajes([]);
  }, []);

  const seleccionarConversacion = useCallback(
    async (id) => {
      if (id === activeId) return;
      setActiveId(id);
      setLoadingConv(true);
      try {
        const body = await asistenteIA.obtenerConversacion(id);
        setActiveMensajes((body.data.mensajes || []).map(mapMensaje));
      } catch {
        setActiveMensajes([]);
      } finally {
        setLoadingConv(false);
      }
    },
    [activeId]
  );

  const eliminarConversacion = useCallback(async (id) => {
    try {
      await asistenteIA.eliminarConversacion(id);
    } catch {
      return;
    }
    setConversaciones((prev) => prev.filter((c) => c._id !== id));
    setActiveId((prev) => {
      if (prev !== id) return prev;
      setActiveMensajes([]);
      return null;
    });
  }, []);

  const enviar = useCallback(
    async (texto) => {
      const pregunta = (texto || '').trim();
      if (!pregunta || loading) return;

      const userMsg = { id: crypto.randomUUID(), role: 'user', text: pregunta };
      setActiveMensajes((prev) => [...prev, userMsg]);
      setLoading(true);

      try {
        const body = await asistenteIA.preguntar(pregunta, activeId);
        const { id_conversacion: idConversacion, titulo, text, chart, charts, rawData } = body.data;

        setActiveMensajes((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text,
            chart: chart || null,
            charts: charts && charts.length > 0 ? charts : chart ? [chart] : [],
            rawData: rawData || null,
          },
        ]);

        if (!activeId) {
          setActiveId(idConversacion);
          setConversaciones((prev) => [{ _id: idConversacion, titulo, updatedAt: new Date().toISOString() }, ...prev]);
        } else {
          setConversaciones((prev) =>
            prev.map((c) => (c._id === idConversacion ? { ...c, updatedAt: new Date().toISOString() } : c))
          );
        }
      } catch (err) {
        setActiveMensajes((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: err.message || 'Tuve un problema conectando con el servidor. Intenta de nuevo.',
            error: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [activeId, loading]
  );

  const activeConv = conversaciones.find((c) => c._id === activeId) || null;

  const value = {
    isOpen,
    open,
    close,
    toggle,
    conversaciones,
    activeId,
    activeConv,
    messages: activeMensajes,
    loading,
    loadingConv,
    nuevaConversacion,
    seleccionarConversacion,
    eliminarConversacion,
    enviar,
  };

  return <IAChatContext.Provider value={value}>{children}</IAChatContext.Provider>;
}

export function useIAChat() {
  const ctx = useContext(IAChatContext);
  if (!ctx) throw new Error('useIAChat must be used within IAChatProvider');
  return ctx;
}
